import type { QueryClient } from '@tanstack/react-query';

export type CartCacheSnapshot = { key: readonly unknown[]; prev: unknown };

export const CART_QUERY_KEYS = ['cart', 'carts'] as const;

// Matches both the by-id cart query (['cart', cartId]) and the carts-list query
// (['carts', locationId]) — the two keys were split so placing an order can invalidate
// the list without refetching the by-id cart into a 404.
export const isCartQuery = (query: { queryKey: readonly unknown[] }) =>
  CART_QUERY_KEYS.some((cartQueryKey) => cartQueryKey === query.queryKey[0]);

export function patchCartCaches<C extends { id: string }>(
  qc: QueryClient,
  cartId: string,
  transform: (cart: C) => C
): CartCacheSnapshot[] {
  const snapshots: CartCacheSnapshot[] = [];
  qc.getQueriesData<unknown>({ predicate: isCartQuery }).forEach(
    ([key, value]) => {
      if (!value) return;
      const v = value as { cart?: C | null } | { carts?: C[] | null };
      if ('cart' in v && v.cart && v.cart.id === cartId) {
        snapshots.push({ key, prev: value });
        qc.setQueryData(key, { ...v, cart: transform(v.cart) });
      } else if (
        'carts' in v &&
        Array.isArray(v.carts) &&
        v.carts.some((c) => c.id === cartId)
      ) {
        snapshots.push({ key, prev: value });
        const next = v.carts.map((c) => (c.id === cartId ? transform(c) : c));
        qc.setQueryData(key, { ...v, carts: next });
      }
    }
  );
  return snapshots;
}

export async function patchCartCachesDroppingStaleReads<
  C extends { id: string },
>(qc: QueryClient, cartId: string, transform: (cart: C) => C): Promise<void> {
  await qc.cancelQueries({ predicate: isCartQuery });
  patchCartCaches(qc, cartId, transform);
}

export function restoreCartCaches(
  qc: QueryClient,
  snapshots: CartCacheSnapshot[]
): void {
  snapshots.forEach(({ key, prev }) => qc.setQueryData(key, prev));
}
